import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Check, FileText, Table2, ClipboardCheck } from "lucide-react";
import EmailOptin from "../components/site/EmailOptin";
import { FadeUp, MaskedLines } from "../components/site/Reveal";
import { DISCLOSURE_FTC, SITE } from "../data/site";
import { PLATFORMS } from "../data/platforms";

const PageShell = ({ overline, title, children, testid }) => (
  <main className="mx-auto max-w-3xl px-6 pt-40 pb-28" data-testid={testid}>
    <p className="overline-label mb-5">{overline}</p>
    <h1 className="font-display text-4xl font-black tracking-tighter md:text-5xl">
      <MaskedLines lines={[title]} />
    </h1>
    <div className="mt-10 space-y-5 text-base leading-[1.85] text-white/65">{children}</div>
  </main>
);

export const AboutPage = () => {
  useEffect(() => { document.title = `About | ${SITE.name}`; }, []);
  return (
    <PageShell overline="About Us" title="Reviews we'd bet our own money on" testid="about-page">
      <p>{SITE.name} exists because choosing email software in 2026 is harder than it should be. Vendor websites all claim 99% deliverability and "powerful automation." Review sites too often rank whoever pays the highest commission.</p>
      <p>We do it differently. Every platform on this site was tested hands-on: we built real automation workflows, sent seed-test campaigns to measure inbox placement, stress-tested support channels with real questions, and calculated true costs at multiple list sizes.</p>
      <FadeUp className="rounded-2xl border border-white/[0.07] bg-cardink p-7">
        <p className="overline-label mb-4 !text-volt">Our Editorial Promise</p>
        <ul className="space-y-3">
          {[
            "Scores are set before we check commission rates",
            "Every review lists real cons — no platform is perfect",
            "We re-test and update every review quarterly",
            "Affiliate links never change our rankings",
          ].map((t, i) => (
            <li key={i} className="flex items-start gap-2.5 text-sm text-white/75">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-volt" strokeWidth={3} />{t}
            </li>
          ))}
        </ul>
      </FadeUp>
      <p>We earn commissions when you purchase through our links — that's how this site stays free. Read our <Link to="/affiliate-disclosure" className="text-volt underline underline-offset-2">full affiliate disclosure</Link> for details.</p>
    </PageShell>
  );
};

export const PrivacyPage = () => {
  useEffect(() => { document.title = `Privacy Policy | ${SITE.name}`; }, []);
  return (
    <PageShell overline="Legal" title="Privacy Policy" testid="privacy-page">
      <p><strong className="text-white">Last updated: July 2026.</strong> This policy explains what data {SITE.name} collects and how we use it.</p>
      <h2 className="font-display text-xl font-bold text-white">Information we collect</h2>
      <p><strong className="text-white">Email address:</strong> When you download our checklist, we store your email to send the resource and our 4-part platform-selection series. Unsubscribe links appear in every email.</p>
      <p><strong className="text-white">Analytics:</strong> We use privacy-respecting analytics to understand which pages help readers most. No personally identifiable browsing profiles are sold or shared.</p>
      <p><strong className="text-white">Cookies:</strong> We use essential cookies plus affiliate tracking cookies. When you click a partner link (e.g., GetResponse, AWeber), the vendor may set a cookie to attribute your purchase to us — this never costs you anything extra.</p>
      <h2 className="font-display text-xl font-bold text-white">Your rights</h2>
      <p>You may request access to, correction of, or deletion of your personal data at any time by contacting privacy@topemailmarketingtools.com. We honor GDPR, CCPA, and CAN-SPAM requirements.</p>
      <h2 className="font-display text-xl font-bold text-white">Data retention</h2>
      <p>Subscriber emails are retained until you unsubscribe or request deletion. We never sell, rent, or share your email address with third parties for their own marketing.</p>
    </PageShell>
  );
};

export const DisclosurePage = () => {
  useEffect(() => { document.title = `Affiliate Disclosure | ${SITE.name}`; }, []);
  return (
    <PageShell overline="Legal · FTC Compliance" title="Affiliate Disclosure" testid="disclosure-page">
      <FadeUp className="rounded-2xl border border-volt/25 bg-cardink p-7">
        <p className="font-serif-accent text-xl italic leading-relaxed text-volt/90">{DISCLOSURE_FTC}</p>
      </FadeUp>
      <h2 className="font-display text-xl font-bold text-white">How we make money</h2>
      <p>When you click a "Start Free Trial" or similar link on this site and later purchase a subscription, the vendor pays us a commission — typically a recurring percentage of your subscription. This comes out of the vendor's marketing budget, never your pocket.</p>
      <h2 className="font-display text-xl font-bold text-white">Our affiliate partners</h2>
      <ul className="list-disc space-y-1.5 pl-6">
        {PLATFORMS.map((p) => <li key={p.slug}>{p.name}</li>)}
      </ul>
      <h2 className="font-display text-xl font-bold text-white">How this affects our content</h2>
      <p>It doesn't affect our scores. Rankings are determined by our hands-on testing rubric before commercial terms are considered. We regularly rank lower-commission products above higher-commission ones — our MailerLite and Moosend coverage is a good example.</p>
      <p>In accordance with FTC 16 CFR Part 255, all affiliate relationships are disclosed at the top of every review and comparison page. Sponsored content, if ever published, will be labeled "Sponsored" in the headline area.</p>
    </PageShell>
  );
};

const RESOURCES = [
  {
    icon: ClipboardCheck,
    title: "Email Marketing Checklist",
    desc: "Our flagship 10-point pre-launch checklist — authentication, welcome flows, segmentation, and list hygiene. PDF + Notion template.",
    cta: "Get it free",
    href: "/#checklist",
  },
  {
    icon: Table2,
    title: "Platform Comparison Chart",
    desc: "The full 2026 comparison matrix: pricing, free plans, automation depth, support channels, and deliverability scores for all five platforms.",
    cta: "View the matrix",
    href: "/#matrix-section",
  },
  {
    icon: FileText,
    title: "Guides & Tutorials",
    desc: "Six practical guides: Email Marketing 101, automation workflows, segmentation strategies, A/B testing, list building, and deliverability deep-dives.",
    cta: "Browse guides",
    href: "/guides",
  },
];

export const ResourcesPage = () => {
  useEffect(() => { document.title = `Resources | ${SITE.name}`; }, []);
  return (
    <main className="mx-auto max-w-5xl px-6 pt-40 pb-28" data-testid="resources-page">
      <p className="overline-label mb-5">Resources</p>
      <h1 className="font-display text-4xl font-black tracking-tighter md:text-5xl">
        <MaskedLines lines={["Templates, checklists & charts"]} />
      </h1>
      <div className="mt-14 grid gap-6 md:grid-cols-3">
        {RESOURCES.map((r, i) => (
          <FadeUp key={i} delay={i * 0.08} className="flex h-full flex-col rounded-2xl border border-white/[0.07] bg-cardink p-8" data-testid={`resource-${i}`}>
            <r.icon className="h-6 w-6 text-volt" />
            <h2 className="mt-4 font-display text-xl font-bold">{r.title}</h2>
            <p className="mt-3 flex-1 text-sm leading-relaxed text-white/55">{r.desc}</p>
            {r.href ? (
              <Link to={r.href} data-testid={`resource-cta-${i}`} className="mt-6 inline-flex w-fit rounded-full border border-volt/40 px-5 py-2 text-xs font-bold text-volt transition-colors hover:bg-volt hover:text-ink">
                {r.cta}
              </Link>
            ) : (
              <span data-testid={`resource-cta-${i}`} className="mt-6 inline-flex w-fit rounded-full border border-white/15 px-5 py-2 text-xs font-bold text-white/40">
                {r.cta}
              </span>
            )}
          </FadeUp>
        ))}
      </div>
      <FadeUp delay={0.2} className="mt-16 rounded-2xl border border-white/[0.07] bg-cardink p-10 text-center">
        <h2 className="font-display text-2xl font-bold">Start with the checklist</h2>
        <div className="mx-auto mt-6 max-w-xl text-left"><EmailOptin source="resources-page" /></div>
      </FadeUp>
    </main>
  );
};

export const NotFoundPage = () => (
  <main className="mx-auto max-w-3xl px-6 pt-48 pb-32 text-center" data-testid="not-found-page">
    <p className="font-serif-accent text-6xl italic text-volt/50">404</p>
    <h1 className="mt-4 font-display text-4xl font-black">This page bounced.</h1>
    <p className="mt-3 text-white/55">Like a bad email address, it doesn't exist.</p>
    <Link to="/" data-testid="not-found-home" className="mt-8 inline-block rounded-full bg-volt px-7 py-3 text-sm font-bold text-ink hover:bg-volt-hover">Back to the reviews</Link>
  </main>
);
